import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { ACTIVE, PAYMENT_FREE } from "const"
import {
    MtUserRegistered,
    MtUserCreate,
    MtAccountCreate,
    MtAccountPackageCreate,
} from "server/models"
import { useInput } from "client/utils"
import {
    StoreType,
    LoadRegisteredUserByCode,
    SaveUser,
    SaveAccount,
    CheckPasswordRestrictions,
    ChangeUserPassword,
    DeleteRegisteredUser,
    SaveAccountPackage,
    AddAccountUser,
} from "client/store"

import { Footer } from "client/parts/layout"
import {
    Loader,
    FormGroup,
    Feedback,
    LostConnection,
} from "client/components"
import {
    AuthWrapper,
    AuthTitle,
    AuthFormWrapper,
    AuthForm,
    AuthFormDescription,
    AuthInput,
    AuthButton,
    SuccessfulAuthWrapper,
    LoginLinkWrapper,
    LoginLink,
} from "./styled"

interface ConfirmEmailProps {
    confirmationCode: string
}

const ConfirmEmail = ({
    confirmationCode,
}: ConfirmEmailProps) => {
    const [password, setPassword] = useInput("")
    const [repeatPassword, setRepeatPassword] = useInput("")

    const [user, setUser] = useState<MtUserRegistered>(null)
    const [passwordMismatch, setPasswordMismatch] = useState(false)
    const [passwordRestrictions, setPasswordRestrictions] = useState(true)
    const [unhandledError, setUnhandledError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isRegistered, setIsRegistered] = useState(false)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const lostServerConnection = useSelector((state: StoreType) => state.system.lostServerConnection)

    useEffect(() => {
        LoadRegisteredUserByCode(confirmationCode)
        .then((res) => {
            setUser(res)
            setIsLoading(false)
        })
    }, [])

    const onConfirm = () => {
        const mismatch = password !== repeatPassword
        setPasswordMismatch(mismatch)

        const correctRestrictions = CheckPasswordRestrictions(password)
        setPasswordRestrictions(correctRestrictions)

        if (mismatch || !correctRestrictions) return

        setIsSaving(true)

        const today = new Date()
        const expiryDate = new Date()
        expiryDate.setDate(expiryDate.getDate() + 30)

        const newUserData: MtUserCreate = {
            firstName: user.firstName,
            lastName: user.lastName,
            login: user.email,
            email: user.email,
            logoId: null,
            status: ACTIVE,
        }

        const newAccountData: MtAccountCreate = {
            name: user.companyName,
            shortName: "",
            logoId: null,
            url: "",
            demoExpiry: expiryDate,
            timezone: "GMT",
            status: ACTIVE,
        }

        let newUserId: number
        let newAccountId: number

        Promise.all([
            SaveUser(null, newUserData, lang).then((res) => {
                newUserId = res.newUserId || null
            }),
            SaveAccount(null, newAccountData, lang).then((res) => {
                newAccountId = res.newAccountId || null
            }),
        ]).then(() => {
            if (!newUserId || !newAccountId) {
                setIsSaving(false)
                setUnhandledError(lang.registrationError)
                return
            }

            const demoAccountPackage: MtAccountPackageCreate = {
                accountId: newAccountId,
                packageId: 1,
                dateStart: today,
                dateEnd: expiryDate,
                paymentOption: PAYMENT_FREE,
                stripePaymentId: null,
                usedSilCalculations: 0,
                additionalSilCalculations: 0,
            }

            Promise.all([
                ChangeUserPassword(newUserId, password),
                DeleteRegisteredUser(user.email),
                SaveAccountPackage(demoAccountPackage, lang), // add demo package
                AddAccountUser(
                    [newUserId],
                    newAccountId,
                    1, // define "maxUsers" as "1" to force allow registered user to be added to new account
                ),
            ]).then(() => {
                setIsRegistered(true)
                setIsSaving(false)
            })
        })
    }

    const allowSetPasswords = password.length && repeatPassword.length

    let companyAccountCreated: string
    let userIsAdmin: string
    if (isRegistered) {
        companyAccountCreated = lang.registrationCompanyAccountCreated.replace("{COMPANY}", user ? user.companyName : "")
        userIsAdmin = lang.registrationUserAdministratorOfAccount
        .replace("{USER}", user ? `${user.firstName} ${user.lastName}` : "")
        .replace("{EMAIL}", user ? user.email : "")
    }

    return (
        <AuthWrapper>
            { lostServerConnection && <LostConnection /> }
            {
                (isLoading || isSaving)
                && <Loader paddingTop={250} />
            }

            <AuthFormWrapper show={!isLoading && !user}>
                <AuthTitle>{lang.userNotFound}</AuthTitle>
                <AuthFormDescription>{lang.userNotFoundDescription}</AuthFormDescription>
            </AuthFormWrapper>

            <AuthFormWrapper show={!isLoading && user && !isRegistered}>
                <AuthTitle>{lang.emailConfirmed}</AuthTitle>
                <AuthForm>
                    <AuthFormDescription>{lang.setPasswordToFinishRegistration}</AuthFormDescription>
                    <FormGroup>
                        <AuthInput
                            type="password"
                            value={password}
                            onChange={setPassword}
                            placeholder={lang.password}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AuthInput
                            type="password"
                            value={repeatPassword}
                            onChange={setRepeatPassword}
                            placeholder={lang.repeat}
                        />
                        <Feedback>{passwordMismatch && lang.passwordMismatch}</Feedback>
                        <Feedback>{!passwordRestrictions && lang.passwordRestrictions}</Feedback>
                        <Feedback>{unhandledError}</Feedback>
                    </FormGroup>

                    <AuthButton
                        variant="success"
                        disabled={!allowSetPasswords}
                        onClick={onConfirm}
                    >
                        {lang.setPassword}
                    </AuthButton>
                </AuthForm>
            </AuthFormWrapper>

            <SuccessfulAuthWrapper show={isRegistered}>
                <AuthTitle>{lang.registrationFinished}</AuthTitle>
                <AuthFormDescription>
                    {companyAccountCreated}
                    <br />
                    {userIsAdmin}
                </AuthFormDescription>
                <LoginLinkWrapper>
                    <LoginLink href="/login">{lang.goToLogin}</LoginLink>
                </LoginLinkWrapper>
            </SuccessfulAuthWrapper>

            <Footer />
        </AuthWrapper>
    )
}
export default ConfirmEmail
